import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, getToken, ReCaptchaV3Provider } from 'firebase/app-check';
import { FIREBASE_CONFIG, RECAPTCHA_SITE_KEY } from './config/apiKeys.js';

let app = null;
let auth = null;
let db = null;
let storage = null;
let functions = null;
let appCheck = null;
let isInitialized = false;

function initializeFirebase() {
  try {
    app = initializeApp(FIREBASE_CONFIG);
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    functions = getFunctions(app, 'us-central1');
    
    // Connect to emulators when running on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // Connect to Auth emulator (only connect once)
      try {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        console.log('✅ Connected to Firebase Auth emulator');
      } catch (error) {
        // Emulator already connected or connection failed (this is okay)
        if (!error.message.includes('already been called')) {
          console.warn('⚠️ Auth emulator connection:', error.message);
        }
      }
      
      // Connect to Firestore emulator (only connect once)
      try {
        connectFirestoreEmulator(db, 'localhost', 8080);
        console.log('✅ Connected to Firestore emulator');
      } catch (error) {
        // Emulator already connected or connection failed (this is okay)
        if (!error.message.includes('already been called')) {
          console.warn('⚠️ Firestore emulator connection:', error.message);
        }
      }

      // Connect to Storage emulator (only connect once)
      try {
        connectStorageEmulator(storage, 'localhost', 9199);
        console.log('✅ Connected to Firebase Storage emulator');
      } catch (error) {
        if (!error.message.includes('already been called')) {
          console.warn('⚠️ Storage emulator connection:', error.message);
        }
      }

      // Connect to Functions emulator
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
        console.log('✅ Connected to Firebase Functions emulator');
      } catch (error) {
        if (!error.message.includes('already been called')) {
          console.warn('⚠️ Functions emulator connection:', error.message);
        }
      }
    }
    
    // Initialize App Check immediately with the hardcoded reCAPTCHA site key
    if (RECAPTCHA_SITE_KEY) {
      try {
        appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (error) {
        console.error('❌ Failed to initialize App Check:', error);
      }
    }

    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    throw new Error('Firebase configuration failed.');
  }
}

export async function getAppCheckToken() {
  if (!appCheck) {
    return null;
  }

  try {
    const token = await getToken(appCheck, false);
    return token.token;
  } catch (error) {
    console.error('❌ Failed to get App Check token:', error);
    return null;
  }
}

// Initialize Firebase immediately
initializeFirebase();

// Export functions that wait for initialization
export async function getAuthInstance() {
  if (!isInitialized) {
    await new Promise(resolve => {
      const checkInit = () => {
        if (isInitialized) {
          resolve();
        } else {
          setTimeout(checkInit, 10);
        }
      };
      checkInit();
    });
  }
  return auth;
}

export async function getDbInstance() {
  if (!isInitialized) {
    await new Promise(resolve => {
      const checkInit = () => {
        if (isInitialized) {
          resolve();
        } else {
          setTimeout(checkInit, 10);
        }
      };
      checkInit();
    });
  }
  return db;
}

// Export the instances directly (for backward compatibility)
export { auth, db, storage, functions };
export default app;